import { type CodeHighlightAdapter, createShikiAdapter } from "@mantine/code-highlight";
import type { LanguageRegistration } from "shiki/core";

/**
 * grammarLoaders are the grammars the highlighter can register, each behind its own
 * dynamic import.
 *
 * shiki needs a language registered before it can tokenize that grammar, and the set covers
 * the languages the documentation and the studio's code blocks actually use (registering a
 * grammar registers its aliases too, so `js` also serves `javascript`). A block whose language
 * is not registered makes shiki throw; the CodeBlock renderer wraps the highlighter in an error
 * boundary that degrades such a block to plain text, so one unknown grammar never takes a page
 * down.
 *
 * **Named loaders rather than a list of language strings.** `import("shiki")` is the FULL
 * bundle: its registry statically references every grammar it ships, so a bundler emits all of
 * them whatever the caller asks to register, and the build carries a syntax definition for every
 * language shiki knows. Naming each import is what lets the bundler see that the rest are
 * unreachable, and it is the reason the set above is a ceiling on what ships rather than only on
 * what resolves.
 */
const grammarLoaders: Record<string, () => Promise<{ default: LanguageRegistration[] }>> = {
  bash: () => import("@shikijs/langs/bash"),
  graphql: () => import("@shikijs/langs/graphql"),
  html: () => import("@shikijs/langs/html"),
  js: () => import("@shikijs/langs/javascript"),
  json: () => import("@shikijs/langs/json"),
  jsx: () => import("@shikijs/langs/jsx"),
  python: () => import("@shikijs/langs/python"),
  tsx: () => import("@shikijs/langs/tsx"),
  typescript: () => import("@shikijs/langs/typescript"),
  vue: () => import("@shikijs/langs/vue"),
};

/**
 * DEFAULT_SHIKI_LANGUAGES is every grammar above, which is what a caller passing nothing gets.
 */
const DEFAULT_SHIKI_LANGUAGES = Object.keys(grammarLoaders);

/**
 * createMarkdocShikiAdapter builds the Mantine code-highlight adapter backed by
 * shiki over the given grammars. shiki is heavy, so it is code-split behind a
 * dynamic import that resolves only when a code block first highlights, keeping
 * it out of the initial bundle. The Mantine adapter supplies its own theme
 * objects per color scheme, so the highlighter is created with the grammars
 * only.
 *
 * The adapter resolves asynchronously. During prerender — a synchronous render
 * with no effects — it never resolves, so the static HTML ships code blocks
 * unhighlighted; the client then loads shiki and highlights them after
 * hydration. Plain, readable code in the crawlable markup is the right trade for
 * SSG and SEO.
 */
export const createMarkdocShikiAdapter = (
  languages: string[] = DEFAULT_SHIKI_LANGUAGES,
): CodeHighlightAdapter =>
  createShikiAdapter(async () => {
    const [{ createHighlighterCore }, { createJavaScriptRegexEngine }] = await Promise.all([
      import("shiki/core"),
      import("@shikijs/engine-javascript"),
    ]);

    // A language this adapter has no loader for is dropped rather than thrown on: the caller
    // asked for a grammar nothing here can supply, and a block in it degrades to plain text
    // exactly as an unregistered one already does.
    const loaders = languages
      .map((language) => grammarLoaders[language])
      .filter((loader) => loader !== undefined);

    return createHighlighterCore({
      // The JavaScript engine rather than Oniguruma: it needs no WASM fetch, which is one
      // fewer network round trip before the first code block can highlight.
      engine: createJavaScriptRegexEngine(),
      langs: await Promise.all(loaders.map((loader) => loader())),
      themes: [],
    });
  });

/**
 * shikiAdapter is the ready-to-use default adapter over the common documentation
 * grammars.
 */
export const shikiAdapter: CodeHighlightAdapter = createMarkdocShikiAdapter();
